import { identityAlxPassMappings, mapAlxToAggregate } from './mapToAggregate'
import { processAlxFiles, readAlxFileContents } from './processAlxFiles'
import type { AlxPassRow, CwCcwMode } from './types'
import { uploadAlxSummary } from '../alxSummaryClient'
import { buildWeldParamsUploadSection } from '../weldUpload/buildWeldParamsSection'
import type { AggregateResult } from '../weldParameterAggregator'
import type { MachineType } from '../weldTxtParser'

export function buildAlxPassMappings(rows: AlxPassRow[]): Record<string, string> {
  return identityAlxPassMappings(Array.from(new Set(rows.map((r) => r.passName))))
}

export interface AlxPreparedUpload {
  rows: AlxPassRow[]
  passMappings: Record<string, string>
  learnedSuggestions: Record<string, string>
  sourceFilenames: string[]
  parseErrors: Array<{ fileName: string; message: string }>
}

export interface AlxUploadResult extends AlxPreparedUpload {
  aggregate: AggregateResult
  uploadedAt: string
}

export interface BatchWeldDraft {
  rowId: string
  weldIdLabel: string
  files: File[]
  rows: AlxPassRow[]
  passMappings: Record<string, string>
  learnedSuggestions?: Record<string, string>
  sourceFilenames: string[]
  /** Telemetry batch uploads store pass names here when `rows` is empty. */
  distinctPassNames?: string[]
  machineType?: MachineType
}

export async function prepareAlxUploadForFiles(input: {
  files: File[]
  cwCcwMode: CwCcwMode
  wpsPassLabels: string[]
  globalSuggestions?: Record<string, string>
}): Promise<AlxPreparedUpload> {
  const contents = await readAlxFileContents(input.files)
  const parsed = processAlxFiles({
    files: input.files,
    contents,
    cwCcwMode: input.cwCcwMode
  })
  const passMappings = buildAlxPassMappings(parsed.rows)
  return {
    rows: parsed.rows,
    passMappings,
    learnedSuggestions: {},
    sourceFilenames: input.files.map((f) => f.name),
    parseErrors: parsed.errors
  }
}

export async function finalizeAlxUploadForFiles(input: {
  projectId: string
  pipeSetId: string
  weldRowId: string
  files: File[]
  rows: AlxPassRow[]
  passMappings: Record<string, string>
  learnedSuggestions?: Record<string, string>
  cwCcwMode: CwCcwMode
}): Promise<AlxUploadResult> {
  const aggregate = mapAlxToAggregate(input.rows, input.passMappings)
  const uploadedAt = new Date().toISOString()
  const sourceFilenames = input.files.map((f) => f.name)
  await uploadAlxSummary({
    projectId: input.projectId,
    pipeSetId: input.pipeSetId,
    weldId: input.weldRowId,
    aggregate,
    passMappings: input.passMappings,
    sourceFilenames,
    cwCcwMode: input.cwCcwMode,
    uploadedAt,
    replaceExisting: true
  })
  return {
    aggregate,
    passMappings: input.passMappings,
    learnedSuggestions: input.learnedSuggestions ?? {},
    sourceFilenames,
    uploadedAt,
    rows: input.rows,
    parseErrors: []
  }
}

export async function runAlxUploadForFiles(input: {
  projectId: string
  pipeSetId: string
  weldRowId: string
  files: File[]
  cwCcwMode: CwCcwMode
  wpsPassLabels: string[]
  globalSuggestions?: Record<string, string>
}): Promise<AlxUploadResult> {
  const prepared = await prepareAlxUploadForFiles({
    files: input.files,
    cwCcwMode: input.cwCcwMode,
    wpsPassLabels: input.wpsPassLabels,
    globalSuggestions: input.globalSuggestions
  })
  return finalizeAlxUploadForFiles({
    projectId: input.projectId,
    pipeSetId: input.pipeSetId,
    weldRowId: input.weldRowId,
    files: input.files,
    rows: prepared.rows,
    passMappings: prepared.passMappings,
    learnedSuggestions: prepared.learnedSuggestions,
    cwCcwMode: input.cwCcwMode
  })
}

export function buildAlxWeldParamsSection(result: AlxUploadResult, cwCcwMode: CwCcwMode) {
  return buildWeldParamsUploadSection({
    aggregate: result.aggregate,
    passMappings: result.passMappings,
    sourceFilenames: result.sourceFilenames,
    uploadedAt: result.uploadedAt,
    uploadFormat: 'alx-summary',
    cwCcwMode
  })
}
